import React, { useCallback, useMemo, useRef, useState } from 'react'

import Button from 'components/Form/Button'
import BasicTable from 'components/Form/Table'
import CardsList from 'components/Form/TableCard'
import CustomTablePagination from 'components/CustomTablePagination'
import useI18n from 'hooks/useI18n'
import { Icon } from 'ui/styled'
import ManualCustomerApprovalDialog from './ManualCustomerApprovalDialog'

import {
  Actions,
  CompanyCell,
  DecisionState,
  DesktopTable,
  EmailLink,
  MobileCards,
  Pagination,
  ReviewBadge,
  ReviewExplanation,
  StatusBadge,
} from './styled'

export default function UserApprovalsTable({
  rows = [],
  rowAction = {},
  rowDecision = {},
  showStateColumn,
  showCepColumn,
  pagination,
  onDecision,
  onPageChange,
  loadCompanyOptions,
  loadIdentityReview,
}) {
  const { t } = useI18n()
  const [manualApprovalRow, setManualApprovalRow] = useState(null)
  const inFlight = useRef(new Set())
  const [automaticRows, setAutomaticRows] = useState({})
  const handleDecision = useCallback(async (row, action) => {
    if (inFlight.current.has(row?.id)) return
    if (action === 'approve' && loadIdentityReview) {
      inFlight.current.add(row.id)
      setAutomaticRows(previous => ({ ...previous, [row.id]: true }))
      try {
        const result = await onDecision(row.id, action, { approvalMode: 'automatic' })
        if (result?.reviewRequired) setManualApprovalRow(row)
      } finally {
        inFlight.current.delete(row.id)
        setAutomaticRows(previous => { const next = { ...previous }; delete next[row.id]; return next })
      }
      return
    }
    if (action === 'approve' && (loadIdentityReview || row?.customerAssociationRequired || row?.approvalFlow === 'partner')) {
      setManualApprovalRow(row)
      return
    }
    onDecision(row?.id, action)
  }, [onDecision, loadIdentityReview])
  const columns = useMemo(() => [
    { title: t('user_approvals_created_at'), ref: 'createdAt' },
    { title: t('user_approvals_name'), ref: 'userName' },
    {
      title: t('user_approvals_email'),
      ref: 'userEmail',
      renderCell: ({ row }) => (
        <EmailLink href={`mailto:${row?.userEmail}`}>{row?.userEmail}</EmailLink>
      ),
    },
    {
      title: t('user_approvals_company'),
      ref: 'companyName',
      renderCell: ({ row }) => (
        <CompanyCell>
          <span>{row?.companyDisplayName || row?.companyName || '-'}</span>
          {!row?.customerAssociationRequired ? null : (
            <>
              <ReviewBadge>{t('user_approvals_manual_review_required')}</ReviewBadge>
              <ReviewExplanation>{row?.customerAssociationReason}</ReviewExplanation>
            </>
          )}
        </CompanyCell>
      ),
    },
    { title: t('user_approvals_country'), ref: 'country' },
    !showStateColumn ? null : { title: t('user_approvals_region'), ref: 'state' },
    !showCepColumn ? null : { title: t('user_approvals_location'), ref: 'cep' },
    {
      title: t('user_approvals_status'),
      ref: 'status',
      renderCell: () => <StatusBadge>{t('pending')}</StatusBadge>,
    },
    {
      title: t('user_approvals_actions'),
      ref: 'actions',
      renderCell: ({ row }) => rowDecision?.[row?.id] ? (
        <DecisionState approved={rowDecision[row.id] === 'approve'}>
          <Icon
            icon={rowDecision[row.id] === 'approve' ? 'success' : 'close'}
            width={16}
            nomargin
          />
          {t(rowDecision[row.id] === 'approve' ? 'user_approvals_approved' : 'user_approvals_rejected')}
        </DecisionState>
      ) : (
        <Actions>
          <Button
            color="blue"
            disabled={!!rowAction?.[row?.id] || !!automaticRows[row?.id]}
            loading={rowAction?.[row?.id] === 'approve' || !!automaticRows[row?.id]}
            nospace
            small
            width="92px"
            onClick={() => handleDecision(row, 'approve')}
          >
            {t('user_approvals_approve')}
          </Button>
          <Button
            color="error"
            outline
            disabled={!!rowAction?.[row?.id] || !!automaticRows[row?.id]}
            loading={rowAction?.[row?.id] === 'reject'}
            nospace
            small
            width="92px"
            onClick={() => handleDecision(row, 'reject')}
          >
            {t('user_approvals_reject')}
          </Button>
        </Actions>
      ),
    },
  ].filter(Boolean), [
    handleDecision,
    automaticRows,
    rowAction,
    rowDecision,
    showCepColumn,
    showStateColumn,
    t,
  ])

  return (
    <>
      <DesktopTable>
        <BasicTable analytical columns={columns} rows={rows} />
      </DesktopTable>
      <MobileCards>
        <CardsList columns={columns} rows={rows} full noShadow />
      </MobileCards>
      {!pagination?.total || pagination.total <= pagination.pageSize ? null : (
        <Pagination>
          <CustomTablePagination
            count={pagination.total}
            page={Math.max(Number(pagination.page || 1) - 1, 0)}
            rowsPerPage={pagination.pageSize}
            onPageChange={onPageChange}
          />
        </Pagination>
      )}
      {!manualApprovalRow ? null : (
        <ManualCustomerApprovalDialog
          row={manualApprovalRow}
          loading={rowAction?.[manualApprovalRow.id] === 'approve'}
          loadCompanyOptions={loadCompanyOptions}
          loadIdentityReview={loadIdentityReview}
          onClose={() => setManualApprovalRow(null)}
          onConfirm={options => onDecision(manualApprovalRow.id, 'approve', options)}
        />
      )}
    </>
  )
}
